/**
 * Case Canvas job runner (increment 1 — frontend orchestration).
 *
 * Creates Run-Rail jobs and executes them. HONESTY CONTRACT (spec guardrail #7):
 * we never fabricate backend output. On this synthetic-demo surface, operators
 * focus/annotate the EXISTING synthetic case and report their real binding +
 * status; the one fully-real end-to-end path is E1 (bank-analyser import). When
 * the backend job façade + fiat services land, each runner swaps to the live call.
 *
 * Day 22–23: O1/O2/O3 LIVE — canvas jobs promote onto case_entities/case_edges.
 * W3-07: O12 (`case_worker`) LIVE — the deep case-worker run as a background
 * canvas job, polled until it ends; the case canvas header follows the same job.
 * W3-11: O4 triage, O5 identity lookup, O9 notice, O11 seal, O14 MuleHunter screen
 * and O15 Instagram enrichment are background canvas jobs bound to existing
 * services (`BOUND_JOB_OPERATORS`). An operator that is not LIVE never runs: the
 * rail says it is unavailable and why.
 */
import type cytoscape from 'cytoscape';
import { useFusionStore } from '../stores/useFusionStore';
import { useIntakeStore } from '../stores/useIntakeStore';
import { STATUS_META, ALGORITHMS, type Operator, type EvidenceType, type NodeType } from './registry';
import { normalizeIdentifier } from './entityJoin';
import { resolveWalletChain } from './chainResolve';
import { runLayout, fitTo, visibleEles, bloom } from '../components/fusion/fusionLayout';
import {
  fetchCaseGraph, envelopeToElements,
  runUtrStitchJob, runOfframpMatchJob, runAutoInvestigateJob,
} from '../services/caseCanvas';
import { openBuildEvidenceCard } from './openBuildCard';
import { caseWriteBlockedNow, VIEWER_ACTION_REASON } from './caseAccess';
import { canvasJobService, type CanvasJobEnvelope } from '../services/api';
import { describeJobProgress, runErrorMessage, watchCanvasJob } from '../components/cases/caseRuns';

/**
 * Window event announcing a background case-worker job started from the rail.
 * A page that follows the job (the case canvas header) calls `preventDefault()`
 * and refetches the graph itself when the job succeeds.
 */
export const CASE_WORKER_JOB_EVENT = 'fusion:case-worker-job';

/** W3-11 background operators bound to existing backend services. */
export const BOUND_JOB_OPERATORS = [
  'triage', 'identity_lookup', 'notice', 'seal', 'mulehunter_scan', 'instagram_promote',
] as const;

/**
 * Every canvas-job operator this runner can start. A LIVE registry entry must name
 * one of these (`jobOperator`), otherwise it would be a button that only posts a
 * message. Asserted by bindingsLedger.test.ts.
 */
export const RUNNABLE_JOB_OPERATORS: readonly string[] = [
  'utr_stitch', 'offramp_match', 'auto_investigate', 'case_worker', ...BOUND_JOB_OPERATORS,
];
export interface CaseWorkerJobEventDetail { caseId: string; jobId: string }

let seq = 0;
const jid = () => `job-${Date.now()}-${seq++}`;
const short = (s: string) => (s.length > 16 ? s.slice(0, 15) + '…' : s);
const cy = () => (window as unknown as { __fusionCy?: cytoscape.Core }).__fusionCy;

/** Operators that, on the synthetic seed, reveal a connected node of this type (nice demo focus). */
const REVEAL: Record<string, NodeType> = { O2: 'exchange', O6: 'handle', O7: 'wallet' };

function focus(nodeId: string, revealType?: NodeType) {
  const c = cy(); if (!c) return;
  const n = c.getElementById(nodeId);
  if (n.empty()) return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let target: any = n;
  if (revealType) {
    const neighbor = n.connectedEdges().connectedNodes(`[type="${revealType}"]`);
    if (!neighbor.empty()) target = neighbor;
  }
  c.animate({ center: { eles: target } }, { duration: 260 });
  const firstId = target.nodes().first().id();
  if (firstId) useFusionStore.getState().select({ id: firstId, kind: 'node' });
}

/** Pull fiat legs for UTR-Stitch from the account node's data bag (if present). */
function stitchEventsFromNode(nodeId: string): Record<string, unknown>[] {
  const c = cy();
  if (!c) return [];
  const n = c.getElementById(nodeId);
  if (n.empty()) return [];
  const data = n.data() as Record<string, unknown>;
  const direct = data.stitch_events || data._stitch_events;
  if (Array.isArray(direct)) {
    return direct.filter((e): e is Record<string, unknown> => !!e && typeof e === 'object');
  }
  // Bank-import rows may stash UTR on _txns / expanded legs.
  const txns = data._txns;
  if (!Array.isArray(txns)) return [];
  const accountRef =
    String(data.account_ref || data.label || nodeId.replace(/^.*:/, ''));
  const out: Record<string, unknown>[] = [];
  for (let i = 0; i < txns.length; i++) {
    const t = txns[i] as Record<string, unknown>;
    const utr = t.utr || t.ref_number || t.rrn;
    if (!utr) continue;
    const dirRaw = String(t.direction || '').toLowerCase();
    const direction =
      dirRaw === 'dr' || dirRaw === 'out' || dirRaw === 'debit' ? 'Dr'
        : dirRaw === 'cr' || dirRaw === 'in' || dirRaw === 'credit' ? 'Cr'
          : null;
    if (!direction) continue;
    out.push({
      event_id: String(t.id || `tx-${i}`),
      direction,
      account_ref: String(t.account_ref || accountRef),
      amount: Number(t.amount ?? t.value ?? 0) || 0,
      utr: String(utr),
      counterparty_ref: t.counterparty_ref || t.counterparty || t.vpa,
      ts: t.ts || t.txn_date,
    });
  }
  return out;
}

async function hydrateFromCaseGraph(caseId: string) {
  const c = cy();
  if (!c) return;
  try {
    const g = await fetchCaseGraph(caseId);
    const els = envelopeToElements(g.nodes || [], g.edges || []);
    const added: string[] = [];
    for (const el of els) {
      const id = String((el.data as { id?: string }).id || '');
      if (!id) continue;
      if (c.getElementById(id).empty()) {
        c.add(el);
        if (!(el.data as { source?: string }).source) added.push(id);
      } else {
        c.getElementById(id).data(el.data as Record<string, unknown>);
      }
    }
    if (added.length) {
      runLayout(c, visibleEles(c));
      fitTo(c, visibleEles(c), 55);
      bloom(c, added.slice(0, 8));
    }
  } catch { /* projection optional */ }
}

async function runUtrStitchLive(
  op: Operator,
  target: { nodeId: string; nodeType: string },
  railJobId: string,
) {
  const caseId = useIntakeStore.getState().caseId;
  const store = useFusionStore.getState();
  if (!caseId) {
    store.updateJob(railJobId, {
      status: 'done',
      message: `LIVE · ${op.binding} — needs a bound case (Fusion sandbox / case canvas).`,
    });
    return;
  }
  const events = stitchEventsFromNode(target.nodeId);
  if (!events.length) {
    store.updateJob(railJobId, {
      status: 'done',
      message:
        'LIVE · utr_stitch — no Dr/Cr legs with UTRs on this account node. '
        + 'Attach stitch_events (or bank legs with utr) then re-run.',
    });
    return;
  }
  try {
    const done = await runUtrStitchJob(
      caseId,
      { events },
      target.nodeId,
      (ev) => {
        if (ev.type === 'progress' && ev.payload?.message) {
          store.updateJob(railJobId, { message: String(ev.payload.message) });
        }
      },
    );
    await hydrateFromCaseGraph(caseId);
    const nEdges = Number(done.edges ?? done.stitch_edges ?? 0);
    store.updateJob(railJobId, {
      status: 'done',
      message: `LIVE · promoted ${nEdges} TRACE edge(s) → case graph (reload-safe)`,
    });
  } catch (e) {
    store.updateJob(railJobId, {
      status: 'error',
      message: e instanceof Error ? e.message : 'utr_stitch failed',
    });
  }
}

async function runOfframpMatchLive(
  op: Operator,
  target: { nodeId: string; nodeType: string },
  railJobId: string,
) {
  const caseId = useIntakeStore.getState().caseId;
  const store = useFusionStore.getState();
  if (!caseId) {
    store.updateJob(railJobId, {
      status: 'done',
      message: `LIVE · ${op.binding} — needs a bound case.`,
    });
    return;
  }
  const c = cy();
  const n = c?.getElementById(target.nodeId);
  const data = (n && !n.empty() ? n.data() : {}) as Record<string, unknown>;
  const label = String(data.label || target.nodeId);
  const params: { vpa?: string; account?: string } = {};
  if (target.nodeType === 'vpa' || label.includes('@') || target.nodeId.includes('vpa:')) {
    params.vpa = label.includes('@') ? label : String(data.canonical_id || label);
  } else {
    params.account = String(data.account_ref || data.canonical_id || label.replace(/^[•*\s]+/, ''));
  }
  try {
    const done = await runOfframpMatchJob(caseId, params, target.nodeId, (ev) => {
      if (ev.type === 'progress' && ev.payload?.message) {
        store.updateJob(railJobId, { message: String(ev.payload.message) });
      }
    });
    await hydrateFromCaseGraph(caseId);
    store.updateJob(railJobId, {
      status: 'done',
      message: done.matched
        ? `LIVE · off-ramp matched · ${done.edges || 0} edge(s) on case graph`
        : 'LIVE · no registry match (data boundary) — identifiers still registered',
    });
  } catch (e) {
    store.updateJob(railJobId, {
      status: 'error',
      message: e instanceof Error ? e.message : 'offramp_match failed',
    });
  }
}

async function runAutoInvestigateLive(
  op: Operator,
  target: { nodeId: string; nodeType: string },
  railJobId: string,
) {
  const caseId = useIntakeStore.getState().caseId;
  const store = useFusionStore.getState();
  if (!caseId) {
    store.updateJob(railJobId, {
      status: 'done',
      message: `LIVE · ${op.binding} — needs a bound case.`,
    });
    return;
  }
  const c = cy();
  const n = c?.getElementById(target.nodeId);
  const data = (n && !n.empty() ? n.data() : {}) as Record<string, unknown>;
  let address = String(data.address || data.canonical_id || '');
  if (!address) {
    const id = target.nodeId;
    address = id.includes(':') ? id.split(':').slice(-1)[0] : id;
  }
  const chain = data.chain ? String(data.chain) : undefined;
  try {
    const done = await runAutoInvestigateJob(
      caseId,
      { address, chain },
      target.nodeId,
      (ev) => {
        if (ev.type === 'progress' && ev.payload?.message) {
          store.updateJob(railJobId, { message: String(ev.payload.message) });
        }
      },
    );
    await hydrateFromCaseGraph(caseId);
    store.updateJob(railJobId, {
      status: 'done',
      message: `LIVE · promoted subject (+ ${done.edges || 0} path edge(s)) → case graph`,
    });
  } catch (e) {
    store.updateJob(railJobId, {
      status: 'error',
      message: e instanceof Error ? e.message : 'auto_investigate failed',
    });
  }
}

/**
 * O12 — the case's Auto-Investigate run as the `case_worker` background job.
 * Starts it (or follows the run already going for the case, R4) and keeps the
 * Run-rail chip current from GET /canvas/{id}/jobs/{job_id} every 5 s.
 */
async function runCaseWorkerLive(op: Operator, railJobId: string) {
  const caseId = useIntakeStore.getState().caseId;
  const store = useFusionStore.getState();
  if (!caseId) {
    store.updateJob(railJobId, {
      status: 'done',
      message: `LIVE · ${op.binding} — needs a bound case (open a case canvas).`,
    });
    return;
  }
  let started: CanvasJobEnvelope;
  try {
    started = await canvasJobService.start(caseId, op.jobOperator || 'case_worker', { params: {} });
  } catch (e) {
    store.updateJob(railJobId, { status: 'error', message: `case_worker could not start: ${runErrorMessage(e)}` });
    return;
  }
  const jobId = started.job_id;
  store.updateJob(railJobId, {
    message: started.existing
      ? `LIVE · a run is already going for this case (job ${short(jobId)}); following it`
      : `LIVE · job ${short(jobId)} started`,
  });
  const detail: CaseWorkerJobEventDetail = { caseId, jobId };
  const followedByPage = !window.dispatchEvent(new CustomEvent(CASE_WORKER_JOB_EVENT, { detail, cancelable: true }));

  const stop = watchCanvasJob({
    caseId,
    jobId,
    fetchJob: canvasJobService.get,
    onUpdate: (job) => {
      if (useIntakeStore.getState().caseId !== caseId) { stop(); return; }   // case unbound: stop following
      const line = describeJobProgress(job.progress);
      if (line) useFusionStore.getState().updateJob(railJobId, { message: `LIVE · ${line}` });
    },
    onDone: (job) => {
      const ok = job.status === 'succeeded';
      useFusionStore.getState().updateJob(railJobId, {
        status: ok ? 'done' : 'error',
        message: ok
          ? 'LIVE · run recorded (case report and Runs list); graph refreshed'
          : `case_worker ${job.status}: ${job.error || 'no error text was recorded'}`,
      });
      if (ok && !followedByPage) void hydrateFromCaseGraph(caseId);
    },
    onError: (message, fatal) => {
      if (fatal) useFusionStore.getState().updateJob(railJobId, { status: 'error', message: `case_worker: ${message}` });
    },
  });
}

function nodeData(nodeId: string): Record<string, unknown> {
  const n = nodeId ? cy()?.getElementById(nodeId) : undefined;
  return (n && !n.empty() ? n.data() : {}) as Record<string, unknown>;
}

function bareId(nodeId: string): string {
  if (nodeId.startsWith('ent:')) return nodeId.split(':').slice(2).join(':');
  return nodeId.includes(':') ? nodeId.split(':').slice(1).join(':') : nodeId;
}

type BoundParams = { params: Record<string, unknown> } | { error: string };

/** Params for a W3-11 bound operator, read from the target node. Exported for tests. */
export async function boundJobParams(
  jobOperator: string,
  target: { nodeId: string; nodeType: string },
): Promise<BoundParams> {
  const data = nodeData(target.nodeId);
  const nodeType = String(target.nodeType || data.type || '');
  switch (jobOperator) {
    case 'triage': {
      const address = normalizeIdentifier('wallet', String(data.address || data.canonical_id || bareId(target.nodeId)));
      if (!address) return { error: 'triage: this node carries no wallet address' };
      const chain = await resolveWalletChain(address, data.chain ? String(data.chain) : null);
      if (!chain.ok) return { error: `triage: ${chain.reason}` };
      return { params: { address, chain: chain.chain } };
    }
    case 'identity_lookup': {
      const type = ['phone', 'vpa', 'email'].includes(nodeType) ? nodeType : '';
      const value = String(data.canonical_id || data.label || bareId(target.nodeId)).trim();
      if (!value) return { error: 'identity lookup: this node carries no identifier' };
      return { params: type ? { value, type } : { value } };
    }
    case 'notice': {
      const exchange = String(data.entity_name || data.exchange_name || data.vasp || data.label || '').trim();
      if (!exchange) return { error: 'notice: this node has no exchange name' };
      const params: Record<string, unknown> = { exchange_name: exchange };
      const addr = String(data.address || '').trim();
      if (addr) {
        params.address = addr;
        if (data.chain) params.chain = String(data.chain);
      }
      return { params };
    }
    case 'seal': {
      const content = String(data.note || data.body || data.text || '');
      if (!content.trim()) {
        return {
          error: 'seal: this document node carries no text to seal (a case note seals its text; '
            + 'an attached file is not kept on the canvas, so it cannot be sealed from here)',
        };
      }
      const title = String(data.title || data.label || 'Document');
      // Same merge key the case-note card persisted (cardRunner runCaseNoteCard).
      const mergeLabel = data.kind === 'case_note' && data.title
        ? `${String(data.title)} · ${content.slice(0, 48)}`
        : String(data.canonical_id || data.label || '');
      return {
        params: {
          content, title,
          ...(mergeLabel ? { canonical_id: normalizeIdentifier('document', mergeLabel) } : {}),
        },
      };
    }
    case 'mulehunter_scan':
    case 'instagram_promote':
      return { params: {} };
    default:
      return { error: `${jobOperator} is not a bound operator` };
  }
}

/** W3-11: start a bound operator as a canvas job and follow it on the Run rail. */
async function runBoundJobLive(op: Operator, target: { nodeId: string; nodeType: string }, railJobId: string) {
  const caseId = useIntakeStore.getState().caseId;
  const jobOperator = op.jobOperator || '';
  if (!caseId) {
    useFusionStore.getState().updateJob(railJobId, {
      status: 'error',
      message: `${op.name} needs a bound case (open a case canvas).`,
    });
    return;
  }
  const built = await boundJobParams(jobOperator, target);
  if ('error' in built) {
    useFusionStore.getState().updateJob(railJobId, { status: 'error', message: built.error });
    return;
  }
  let started: CanvasJobEnvelope;
  try {
    started = await canvasJobService.start(caseId, jobOperator, {
      target_ref: op.scope === 'case' ? undefined : target.nodeId || undefined,
      params: built.params,
    });
  } catch (e) {
    useFusionStore.getState().updateJob(railJobId, {
      status: 'error', message: `${jobOperator} could not start: ${runErrorMessage(e)}`,
    });
    return;
  }
  const jobId = started.job_id;
  useFusionStore.getState().updateJob(railJobId, {
    message: started.existing
      ? `LIVE · a ${jobOperator} job is already running for this case (job ${short(jobId)}); following it`
      : `LIVE · job ${short(jobId)} started`,
  });
  const stop = watchCanvasJob({
    caseId,
    jobId,
    fetchJob: canvasJobService.get,
    onUpdate: (job) => {
      if (useIntakeStore.getState().caseId !== caseId) { stop(); return; }
      const line = describeJobProgress(job.progress);
      if (line) useFusionStore.getState().updateJob(railJobId, { message: `LIVE · ${line}` });
    },
    onDone: (job) => {
      const ok = job.status === 'succeeded';
      const line = typeof job.progress?.message === 'string' ? job.progress.message : '';
      useFusionStore.getState().updateJob(railJobId, {
        status: ok ? 'done' : 'error',
        message: ok
          ? `LIVE · ${line || 'recorded on the case graph'}`
          : `${jobOperator} ${job.status}: ${job.error || 'no error text was recorded'}`,
      });
      if (ok) void hydrateFromCaseGraph(caseId);
    },
    onError: (message, fatal) => {
      if (fatal) useFusionStore.getState().updateJob(railJobId, { status: 'error', message: `${jobOperator}: ${message}` });
    },
  });
}

export function runOperator(op: Operator, target: { nodeId: string; nodeType: string }) {
  const store = useFusionStore.getState();
  const id = jid();
  // Second line behind the disabled controls (the autopilot reaches here too): a viewer never starts a run.
  if (caseWriteBlockedNow()) {
    store.closeRadial();
    store.addJob({ id, operator: op.id, label: op.name, status: 'error', message: VIEWER_ACTION_REASON, ts: Date.now() });
    return;
  }
  const caseScoped = op.scope === 'case';
  store.addJob({
    id, operator: op.id,
    label: caseScoped ? `${op.name} · whole case` : `${op.name} · ${short(target.nodeId)}`,
    targetId: target.nodeId || undefined, status: 'running', ts: Date.now(),
  });
  store.closeRadial();
  if (target.nodeId) focus(target.nodeId, REVEAL[op.id]);

  const meta = STATUS_META[op.status];
  if (meta.live) {
    if (op.jobOperator === 'case_worker') { void runCaseWorkerLive(op, id); return; }
    if (op.jobOperator === 'utr_stitch') { void runUtrStitchLive(op, target, id); return; }
    if (op.jobOperator === 'offramp_match') { void runOfframpMatchLive(op, target, id); return; }
    if (op.jobOperator === 'auto_investigate') { void runAutoInvestigateLive(op, target, id); return; }
    if (op.jobOperator && (BOUND_JOB_OPERATORS as readonly string[]).includes(op.jobOperator)) {
      void runBoundJobLive(op, target, id);
      return;
    }
  }
  // Not runnable: say so (the palette and radial menu render these disabled; the
  // playbook autopilot still reaches here and gets the honest status).
  store.updateJob(id, {
    status: 'error',
    message: `${meta.live ? 'Not bound' : meta.label} · unavailable — ${op.unavailable || op.binding}`,
  });
}

/** Palette evidence action. Opens Build (or Import for E1) so the officer gets a real form. */
export function runEvidence(ev: EvidenceType): boolean {
  const store = useFusionStore.getState();
  const id = jid();

  // Bank statement — Import tab is the canvas-side StatementFlow surface.
  if (ev.id === 'E1') {
    window.dispatchEvent(new CustomEvent('fusion:open-tab', { detail: 'import' }));
    store.addJob({
      id, operator: ev.id, label: ev.name, status: 'done',
      message: 'Opened Bank import — drop a PDF/XLSX/CSV (or use Build → Bank Statement card)',
      ts: Date.now(),
    });
    return true;
  }

  // Everything with a form (paste / file / json) opens Build with that card ready.
  // Disabled only when status is new/roadmap AND the row cannot produce a node.
  const disabled = (ev.status === 'new' || ev.status === 'roadmap') && !ev.produces;
  if (disabled) {
    store.addJob({
      id, operator: ev.id, label: ev.name, status: 'done',
      message: `${STATUS_META[ev.status].label} · ${ev.binding}`,
      ts: Date.now(),
    });
    return false;
  }

  openBuildEvidenceCard({ registryId: ev.id });
  store.addJob({
    id, operator: ev.id, label: `${ev.name} · Build`,
    status: 'done',
    message: 'Opened on Build — fill the card and press Run',
    ts: Date.now(),
  });
  return false;
}

/**
 * Palette Operators row — run against the currently selected canvas node when the
 * type matches. Otherwise post a clear Run-rail hint (right-click still works).
 */
export function runOperatorFromPalette(op: Operator) {
  const store = useFusionStore.getState();
  const sel = store.selected;
  if (!STATUS_META[op.status].live) {
    store.addJob({
      id: jid(), operator: op.id, label: op.name, status: 'error',
      message: `${STATUS_META[op.status].label} · unavailable — ${op.unavailable || op.binding}`,
      ts: Date.now(),
    });
    return;
  }
  // Case-scoped operators run on the whole case: no node needs to be selected.
  if (op.scope === 'case') {
    runOperator(op, { nodeId: sel && sel.kind === 'node' ? sel.id : '', nodeType: '' });
    return;
  }
  if (!sel || sel.kind !== 'node') {
    const id = jid();
    store.addJob({
      id, operator: op.id, label: op.name, status: 'error',
      message: `Select a ${op.availableOn.join(' / ')} node first, or right-click one`,
      ts: Date.now(),
    });
    return;
  }
  const c = cy();
  const n = c?.getElementById(sel.id);
  if (!c || !n || n.empty()) {
    const id = jid();
    store.addJob({
      id, operator: op.id, label: op.name, status: 'error',
      message: 'Selected node is not on the canvas — open Canvas and select again',
      ts: Date.now(),
    });
    return;
  }
  const nodeType = String(n.data('type') || '');
  if (!op.availableOn.includes(nodeType as NodeType)) {
    const id = jid();
    store.addJob({
      id, operator: op.id, label: op.name, targetId: sel.id, status: 'error',
      message: `Needs ${op.availableOn.join(' / ')} — selected node is ${nodeType || 'unknown'}`,
      ts: Date.now(),
    });
    return;
  }
  runOperator(op, { nodeId: sel.id, nodeType });
}

/** Algorithms are not runnable yet — honest Run-rail note (never fake output). */
export function runAlgorithmFromPalette(algoId: string) {
  const a = ALGORITHMS.find((x) => x.id === algoId);
  const store = useFusionStore.getState();
  const id = jid();
  if (!a) {
    store.addJob({ id, operator: algoId, label: algoId, status: 'error', message: 'Unknown algorithm', ts: Date.now() });
    return;
  }
  const meta = STATUS_META[a.status];
  store.addJob({
    id, operator: a.id, label: a.name, status: 'done',
    message: `${meta.label} · ${a.binding}`,
    ts: Date.now(),
  });
}
